import {
  trigger,
  state,
  style,
  transition,
  animate,
} from "@angular/animations";
import {
  CommonModule,
  isPlatformBrowser,
  NgOptimizedImage,
} from "@angular/common";
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  PLATFORM_ID,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ButtonComponent } from "../button/button.component";
import { NavigationEnd, Router, RouterLink } from "@angular/router";
import { ProductQuantityComponent } from "../product-quantity/product-quantity.component";
import { DiscountPipe } from "../../pipes/discount.pipe";
import { ShoppingCart, ItemShoppingCart } from "../../models/item-cart.model";
import { ShoppingCartService } from "../../services/cart-service.service";
import { IconComponent } from "../icon/icon.component";

@Component({
  selector: "app-cart",
  templateUrl: "./cart.component.html",
  imports: [
    CommonModule,
    ButtonComponent,
    RouterLink,
    ProductQuantityComponent,
    DiscountPipe,
    NgOptimizedImage,
    IconComponent,
  ],
  animations: [
    trigger("fadeInOut", [
      state(
        "inactive",
        style({
          opacity: 0,
          transform: "translateX(100%)",
        }),
      ),
      state(
        "active",
        style({
          opacity: 1,
          transform: "translateX(0)",
        }),
      ),
      transition("inactive => active", [animate("200ms ease-in-out")]),
      transition("active => inactive", [animate("300ms ease-in-out")]),
    ]),
  ],
})
export class CartComponent implements OnInit {
  readonly MAX_QUANTITY = 20;
  readonly MIN_QUANTITY = 0;
  businessColor = "orange";
  private _shoppingCartService = inject(ShoppingCartService);
  private _router = inject(Router);
  private PLATAFORMID = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  isProductPage = false;

  /** Derived animation state — driven by the cartOpen signal. */
  readonly state = computed(() =>
    this._shoppingCartService.cartOpen() ? "active" : "inactive",
  );

  /** Derived cart snapshot with all computed totals. */
  readonly shoppingCart = computed(() => {
    const c = this._shoppingCartService.shoppingCart();
    return {
      ...c,
      totalItems: this._shoppingCartService.getTotalItemsByShoppingCart(c),
      total: this._shoppingCartService.getTotalByShoppingCart(c),
      subTotal: this._shoppingCartService.getSubTotalByShoppingCart(c),
      totalDiscount:
        this._shoppingCartService.getTotalDiscountByShoppingCart(c),
    };
  });

  ngOnInit() {
    if (isPlatformBrowser(this.PLATAFORMID)) {
      if (window.location.pathname == "/productos") {
        this.isProductPage = true;
      } else {
        this.isProductPage = false;
      }
    }

    this._router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const currentUrl =
            event.url.split("/").pop()?.split("?").shift() || "";
          if (
            currentUrl === "productos" ||
            window.location.pathname == "productos"
          ) {
            this.isProductPage = true;
          } else {
            this.isProductPage = false;
          }
        }
      });
  }

  toggle() {
    this._shoppingCartService.toggleCart();
  }

  get TextColorClass() {
    const colorsList: Record<string, string> = {
      indigo: "text-indigo-500",
      gray: "text-fg-subtle",
      red: "text-red-500",
      yellow: "text-yellow-500",
      green: "text-green-500",
      blue: "text-blue-500",
      purple: "text-purple-500",
      pink: "text-pink-500",
      sky: "text-sky-500",
      orange: "text-primary-hover",
      teal: "text-teal-500",
      lime: "text-lime-500",
      fuchsia: "text-fuchsia-500",
      black: "text-fg",
    };
    return colorsList[this.businessColor];
  }

  get TextHoverClass() {
    const colorsList: Record<string, string> = {
      indigo: "hover:text-indigo-300",
      gray: "hover:text-fg-subtle",
      red: "hover:text-red-300",
      yellow: "hover:text-yellow-300",
      green: "hover:text-green-300",
      blue: "hover:text-blue-300",
      purple: "hover:text-purple-300",
      pink: "hover:text-pink-300",
      sky: "hover:text-sky-300",
      orange: "hover:text-orange-300",
      teal: "hover:text-teal-300",
      lime: "hover:text-lime-300",
      fuchsia: "hover:text-fuchsia-300",
      black: "hover:text-grey-700",
    };
    return colorsList[this.businessColor];
  }

  quantityValue(value: number, product: ItemShoppingCart) {
    const current = this._shoppingCartService.shoppingCart();
    const updatedItems = current.items.map((item) =>
      item.product.id === product.product.id
        ? { ...item, quantity: value }
        : item,
    );
    this._shoppingCartService.setShoppingCart({
      ...current,
      items: updatedItems,
    });
  }

  deleteItemFromCart(product: ItemShoppingCart) {
    const current = this._shoppingCartService.shoppingCart();
    const updatedItems = current.items.filter(
      (item) => item.product.id !== product.product.id,
    );
    this._shoppingCartService.setShoppingCart({
      ...current,
      items: updatedItems,
    });
  }

  goCheckout() {
    this._shoppingCartService.toggleCart();
    setTimeout(() => {
      this._router.navigate(["/checkout"]);
    }, 300);
  }
}
